import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { PERIOD_KEYS, ReviewsRow, openOnKey } from "./discover/DiscoverAll";
import { CARD_SURFACE } from "./discover/DiscoverCampaignCard";
import { formatMoney } from "./discover/campaignDisplay";
import ProductAppsFolder from "./ProductAppsFolder";
import Icon from "./ds/Icon";
import IconButton from "./ds/IconButton";
import { PRODUCT_EXCLUDED_APP_IDS, managesMintSpace, productFolderApps } from "../utils/mintSpaceApps";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import { useAuth } from "../context/AuthContext";

/**
 * A product on its space's home, kept short so the posts start above the fold: the
 * cover as a square, then name, price and reviews. The space itself is on the page, so
 * the card doesn't repeat it.
 */
const ProductRow = ({ space, product, apps, onOpen, language, t }) => {
  const isFree = product.accessType === "free";
  const media = product.bannerImage || product.listingBanner || "";
  const isVideo = Boolean(media) && (product.bannerImage ? product.bannerType : product.listingBannerType) === "video";
  const billing = isFree
    ? ""
    : product.billingType === "recurring"
    ? ` / ${t(PERIOD_KEYS[product.period] || PERIOD_KEYS["1m"])}`
    : ` · ${t("home.oneTime")}`;
  const open = () => onOpen(`/mint-space/${space._id}/product/${product._id}`);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={openOnKey(open)}
      className={`${CARD_SURFACE} cursor-pointer !flex-row items-center gap-space-12 p-space-12`}
    >
      <span className="relative flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-radius-sm bg-fill-quaternary text-label-tertiary">
        {media ? (
          isVideo ? (
            <video src={media} muted playsInline className="h-full w-full object-cover" />
          ) : (
            <img src={media} alt="" className="h-full w-full object-cover" />
          )
        ) : (
          <Icon name="bag" size={24} />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <h3 className="truncate text-subheadline-semibold text-label-primary">
          {product.name || t("mintSpace.untitledProduct")}
        </h3>
        <p className="truncate text-footnote text-label-secondary">
          <span className="text-footnote-semibold text-label-primary">
            {isFree ? t("mintSpace.free") : formatMoney(product.price ?? 0, language)}
          </span>
          {billing}
        </p>
        <ReviewsRow average={product.averageRating} count={product.ratingCount ?? 0} language={language} t={t} />
        {apps?.length > 0 && (
          <span className="mt-space-4 self-start" onClick={(event) => event.stopPropagation()}>
            <ProductAppsFolder
              spaceId={space._id}
              title={product.name}
              apps={apps}
              buttonClassName="inline-flex items-center gap-1 rounded-radius-xs bg-fill-secondary px-1.5 py-0.5 text-caption-1-semibold text-label-primary transition hover:bg-fill-primary"
            />
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Horizontal, arrow-scrollable row of a Mint Space's products (its appPricing entries).
 * Rendered on the Mint Space home between the header and the post composer. Returns null when
 * the space has no products.
 */
const MintSpaceProductsGrid = ({ mintSpace }) => {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const products = (mintSpace?.appPricing || []).filter(
    (p) => !PRODUCT_EXCLUDED_APP_IDS.includes(p.appId)
  );
  if (!products.length) return null;

  // Its owner and moderators can look inside each product.
  const manages =
    mintSpace.isOwner || mintSpace.isModerator || managesMintSpace(mintSpace, user?._id || user?.id);
  const catalog = manages ? getMintSpaceAppStoreCatalog(t) : null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section className="flex flex-col gap-space-12">
      <div className="flex items-center justify-between gap-space-12">
        <h2 className="text-headline text-label-primary">{t("mintSpace.products")}</h2>
        {products.length > 1 && (
          <div className="flex items-center gap-space-8">
            <IconButton label={t("common.previous")} onClick={() => scrollBy(-1)}>
              <Icon name="chevron-left" size={18} />
            </IconButton>
            <IconButton label={t("common.next")} onClick={() => scrollBy(1)}>
              <Icon name="chevron-right" size={18} />
            </IconButton>
          </div>
        )}
      </div>
      <div ref={scrollerRef} className="scrollbar-hide flex snap-x snap-mandatory gap-space-16 overflow-x-auto">
        {products.map((p) => (
          <div key={p._id} className="flex w-[280px] shrink-0 snap-start">
            <ProductRow
              space={mintSpace}
              product={p}
              apps={manages ? productFolderApps(mintSpace, p, catalog) : null}
              onOpen={navigate}
              language={language}
              t={t}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default MintSpaceProductsGrid;
